// 세션 창 ↔ 메인 창 **접근 토큰 중개** (2026-10-23 사용자 보고: 끊긴 탭의 [다시 연결]이 아무 반응 없음).
//
// ## 왜 필요한가
// 세션 창은 메인 창이 넘긴 요청(`SessionRequest.token` = 접근 토큰, 15분)만 들고 있다. 갱신 토큰(refresh)은 메인 창의 `Api` 만 갖는다.
// 그래서 15분이 지난 뒤 [다시 연결]·탭 옮기기를 하면 만료 토큰으로 서버에 붙다 거부되고, 탭은 같은 문구로 다시 실패했다
// (사용자 눈에는 "아무 동작도 안 함").
// ⚠️ 세션 창이 갱신 토큰을 **따로 들고 직접 갱신하면 안 된다** — 회전형이라 두 창이 같은 갱신 토큰을 쓰면 서버가 재사용(도난)으로
//    보고 로그인 전체를 끊는다(api.ts `onRefreshTokenChanged` 주석). ⇒ 갱신은 늘 메인 창이 하고, 세션 창은 **물어서 받는다**.
//
// ## 통로(환경마다 — bridge.ts 와 같은 구분)
//   - 웹(브라우저): `BroadcastChannel`(같은 오리진 창끼리만). 메인 창 탭이 여럿이면 먼저 온 답을 쓴다.
//   - 통합앱(Tauri): 앱 이벤트. 메인 창은 닫아도 숨기기만 해서(lib.rs CloseRequested) 늘 살아 있다.
//     세션 창은 자기 창 label 을 실어 묻고, 메인 창은 그 창에만(`emitTo`) 답한다.
// ⚠️ 토큰을 로그에 남기지 않는다.
import { IS_SHELL } from '../shell';
import { jwtExpiryMs, TOKEN_REFRESH_MARGIN_MS } from '../api';
import { tokenSubject } from '../trustedDevices';
import { ensureUsableTokenWith } from './tokenPolicy';

/** 웹: 토큰 중개 채널 이름(같은 오리진 전용). */
const TOKEN_CHANNEL = 'rs-session-token';
/** 앱: 세션 창 → 메인 창 요청 이벤트 / 메인 창 → 세션 창 응답 이벤트. 두 쪽이 이 파일을 같이 쓰므로 이름이 어긋날 수 없다. */
const TAURI_TOKEN_REQ_EVENT = 'rs-token-req';
const TAURI_TOKEN_RES_EVENT = 'rs-token-res';
/** 앱: 메인 창 label — 🔴 lib.rs `MAIN_WINDOW_LABEL` 과 같아야 한다. */
const TAURI_MAIN_LABEL = 'main';
/** 메인 창 답을 기다리는 한도(ms). 메인 창이 없으면(브라우저에서 닫힘) 이만큼 기다린 뒤 포기한다. */
export const TOKEN_REQUEST_TIMEOUT_MS = 3_000;

/** 요청·응답 메시지. `token` 이 빈 문자열 = 메인 창이 로그인 상태가 아니다. */
type TokenMsg =
  | { type: 'req'; id: string; from?: string }
  | { type: 'res'; id: string; token: string };

function newId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * 메인 창: 세션 창의 토큰 요청에 답한다. @param fresh 지금 쓸 수 있는 접근 토큰을 돌려주는 함수(`Api.freshToken`,
 *   로그인 전이면 빈 문자열). @returns 정리 함수
 */
export function serveSessionTokens(fresh: () => Promise<string>): () => void {
  const answer = async (id: string, reply: (m: TokenMsg) => void) => {
    let token = '';
    try {
      token = await fresh();
    } catch {
      token = '';
    }
    reply({ type: 'res', id, token });
  };

  if (IS_SHELL) {
    let unlisten: (() => void) | undefined;
    let alive = true;
    void (async () => {
      const { listen, emitTo } = await import('@tauri-apps/api/event');
      const un = await listen<TokenMsg>(TAURI_TOKEN_REQ_EVENT, (e) => {
        const m = e.payload;
        // 세션 창(label `sessions…`)의 요청에만 답한다 — 그리기·채팅 같은 다른 창이 토큰을 받을 이유가 없다.
        if (m?.type !== 'req' || !m.from?.startsWith('sessions')) {
          return;
        }
        void answer(m.id, (r) => void emitTo(m.from as string, TAURI_TOKEN_RES_EVENT, r));
      });
      if (alive) {
        unlisten = un;
      } else {
        un();
      }
    })();
    return () => {
      alive = false;
      unlisten?.();
    };
  }

  const ch = new BroadcastChannel(TOKEN_CHANNEL);
  ch.onmessage = (e: MessageEvent<TokenMsg>) => {
    if (e.data?.type === 'req') {
      void answer(e.data.id, (r) => ch.postMessage(r));
    }
  };
  return () => ch.close();
}

/**
 * 세션 창: 접속에 쓸 접근 토큰을 확보한다 — 규칙은 `tokenPolicy.ensureUsableTokenWith`(넉넉하면 그대로 · 아니면 메인 창에 묻기 ·
 * 못 받거나 다른 계정이면 `LOGIN_EXPIRED_MESSAGE` 로 거절).
 */
export function ensureUsableToken(token: string): Promise<string> {
  return ensureUsableTokenWith(token, {
    ask: requestFreshToken,
    expiryMs: jwtExpiryMs,
    subject: tokenSubject,
    marginMs: TOKEN_REFRESH_MARGIN_MS,
  });
}

/**
 * 세션 창: 메인 창에 지금 쓸 수 있는 접근 토큰을 묻는다.
 * @returns 토큰 — 메인 창이 없거나(시간 초과) 로그인 상태가 아니면 빈 문자열
 */
export async function requestFreshToken(timeoutMs = TOKEN_REQUEST_TIMEOUT_MS): Promise<string> {
  const id = newId();
  if (IS_SHELL) {
    const { emitTo } = await import('@tauri-apps/api/event');
    const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
    const me = getCurrentWebviewWindow().label;
    // 응답은 이 창으로만 온다(`emitTo(me)`) → 창 리스너로 받는다(전역 listen 이면 다른 세션 창 응답까지 본다).
    return new Promise<string>((resolve) => {
      let un: (() => void) | undefined;
      const done = (token: string) => {
        window.clearTimeout(timer);
        un?.();
        resolve(token);
      };
      const timer = window.setTimeout(() => done(''), timeoutMs);
      void getCurrentWebviewWindow()
        .listen<TokenMsg>(TAURI_TOKEN_RES_EVENT, (e) => {
          if (e.payload?.type === 'res' && e.payload.id === id) {
            done(e.payload.token);
          }
        })
        .then((u) => {
          un = u;
          // 리스너를 붙인 **뒤에** 묻는다(답이 먼저 와서 사라지지 않게).
          void emitTo(TAURI_MAIN_LABEL, TAURI_TOKEN_REQ_EVENT, { type: 'req', id, from: me } satisfies TokenMsg);
        })
        .catch(() => done(''));
    });
  }
  return new Promise<string>((resolve) => {
    const ch = new BroadcastChannel(TOKEN_CHANNEL);
    const done = (token: string) => {
      window.clearTimeout(timer);
      ch.close();
      resolve(token);
    };
    const timer = window.setTimeout(() => done(''), timeoutMs);
    ch.onmessage = (e: MessageEvent<TokenMsg>) => {
      if (e.data?.type === 'res' && e.data.id === id) {
        done(e.data.token);
      }
    };
    ch.postMessage({ type: 'req', id } satisfies TokenMsg);
  });
}
